import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import RosterContext from "../../contexts/RosterContext";
import TournamentFormContext from "../../contexts/TournamentFormContext";
import GlobalModalContext from "../../contexts/GlobalModalContext";
import AuthContext from "../../contexts/AuthContext";
import WBCYearsContext from "../../contexts/WBCYearsContext";
import SelectedTeamContext from "../../contexts/SelectedTeamContext";
import { useError } from "../hooks/useError";
import useIsPlayerOnRoster from "../hooks/useIsPlayerOnRoster";
import WBCAdminAccess from "../protected/WBCAdminAccess";
import { QUALIFYING } from "../../constants/TournamentTypeConstants";
import useTournamentType from "../hooks/useTournamentType";

const AddProvisionalContainer = styled.div`
  text-align: center;
  cursor: ${props => (props.disabled ? "no-drop" : "pointer")};
  color: ${props => props.theme[props.disabled ? "grey" : "wbcBlue"]};
`;

const AddProvisionalFormatter = ({ row }) => {
  // hook(s)
  const rosterContext = useContext(RosterContext);
  const tournamentFormContext = useContext(TournamentFormContext);
  const globalModalContext = useContext(GlobalModalContext);
  const authContext = useContext(AuthContext);
  const { selectedWbcYear } = useContext(WBCYearsContext);
  const { tournamentType } = useTournamentType();
  const selectedTeamContext = useContext(SelectedTeamContext);
  const showErrors = useError();

  // variable(s)
  const { players } = rosterContext.state.provisional;
  const { onFormDirty } = tournamentFormContext.state;
  const { team } = selectedTeamContext.state;
  const alreadyOnRoster = useIsPlayerOnRoster(row.profileId);

  const provisionalMax = useMemo(
    () =>
      selectedWbcYear &&
      (tournamentType === QUALIFYING
        ? selectedWbcYear.qualifyingTournamentSettings.rosterNumbers.provisionalMax
        : selectedWbcYear.mainTournamentSettings.rosterNumbers.provisionalMax),
    [selectedWbcYear, tournamentType]
  );

  const finalized = useMemo(() => team.finalizedStatus?.finalized, [team.finalizedStatus]);

  // funtion(s)
  const addToProvisionalRoster = () => {
    globalModalContext.dispatch({
      type: "closeModal"
    });
    rosterContext.dispatch({
      type: "addToProvisionalRoster",
      player: row
    });
    onFormDirty();
  };

  const onClick = () => {
    if (alreadyOnRoster) return;
    if (players.length >= parseInt(provisionalMax)) {
      showErrors(
        `The player cannot be added to the Provisional Roster because it exceeds the ${provisionalMax} player maximum.`,
        addToProvisionalRoster
      );
    } else {
      addToProvisionalRoster();
    }
  };

  return (
    <AddProvisionalContainer
      disabled={alreadyOnRoster}
      onClick={onClick}
      title={alreadyOnRoster ? "Player already on roster" : "Add player to provisional roster"}
    >
      {!finalized || authContext.state.isWBCIadmin ? (
        <WBCAdminAccess includesTc={true}>
          {alreadyOnRoster ? <i className="fas fa-check fa-xs" /> : <i className="fas fa-user-plus fa-xs" />}
        </WBCAdminAccess>
      ) : null}
    </AddProvisionalContainer>
  );
};

AddProvisionalFormatter.propTypes = {
  row: PropTypes.object
};

export default AddProvisionalFormatter;
